import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import api, { API_URL } from "../services/api";
import CategoryPickerModal from "../components/CategoryPickerModal";
import VerificationGate from "../components/VerificationGate";
import {
  CheckCircleIcon,
  CameraIcon,
  PlusIcon,
  XIcon,
  SparklesIcon
} from "../components/Icons";

const SectionTitle = ({ children }) => <div className="form-section-title">{children}</div>;

const Field = ({ label, hint, required, children }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && (
      <label className="form-label">
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
    )}
    {children}
    {hint && <p className="form-hint">{hint}</p>}
  </div>
);

function ProductForm() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = !!id;

  // Query params for draft ID
  const queryParams = new URLSearchParams(location.search);
  const initialDraftId = queryParams.get("draftId");

  const [draftId, setDraftId] = useState(initialDraftId || null);
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle' | 'saving' | 'saved' | 'offline' | 'error'
  const [lastSavedTime, setLastSavedTime] = useState(null);

  // Form Fields
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [metaTag, setMetaTag] = useState("");
  const [keyword, setKeyword] = useState("");
  const [price, setPrice] = useState("");
  const [qualityType, setQualityType] = useState("First Class");
  const [discountActive, setDiscountActive] = useState(false);
  const [discountPrice, setDiscountPrice] = useState("0");
  const [discription, setDiscription] = useState("");
  const [brandName, setBrandName] = useState("");
  const [stock, setStock] = useState("1");
  const [offers, setOffers] = useState("");
  const [isdeliveryFree, setIsdeliveryFree] = useState(false);
  const [shippingCharges, setShippingCharges] = useState("0");
  const [videoUrl, setVideoUrl] = useState("");
  const [materials, setMaterials] = useState("");
  const [highlights, setHighlights] = useState("");

  // Persisted Server Images
  const [mainImages, setMainImages] = useState([]); // [{ public_id, imageurl }]
  const [galleryImages, setGalleryImages] = useState([]); // [{ public_id, imageurl }]
  const [uploadingImage, setUploadingImage] = useState(false);

  // Category taxonomy
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [subcategoryName, setSubcategoryName] = useState("");
  const [productTypeId, setProductTypeId] = useState("");
  const [productTypeName, setProductTypeName] = useState("");
  const [categoryPath, setCategoryPath] = useState("");
  const [customCategoryRequestId, setCustomCategoryRequestId] = useState(null);

  // Dynamic attributes & tags
  const [categoryAttributesDef, setCategoryAttributesDef] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [tags, setTags] = useState([]);
  const [customTagInput, setCustomTagInput] = useState("");

  // Unfinished Drafts recovery state
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [showResumeBanner, setShowResumeBanner] = useState(false);

  // Loading & Error states
  const [readiness, setReadiness] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  // Smart suggestion state
  const [suggestion, setSuggestion] = useState(null);
  const suggestTimerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const isInitialMount = useRef(true);

  const getFullImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("public")) return `${API_URL}/${path}`;
    return `${API_URL}/${path.replace(/^\//, "")}`;
  };

  // Fetch Category Attributes
  const fetchCategoryAttributes = useCallback(async (catId) => {
    try {
      const res = await api.get(`/categories/${catId}`);
      if (res.data.category) {
        setCategoryAttributesDef(res.data.category.effectiveAttributes || res.data.category.attributes || []);
      }
    } catch (err) {
      console.warn("Could not load category attributes:", err);
    }
  }, []);

  const loadDraft = useCallback(async (dId) => {
    try {
      setFetching(true);
      const res = await api.get(`/vendor/products/drafts/${dId}`);
      const d = res.data.draft;
      setDraftId(d._id);
      setName(d.name || "");
      setTag(d.tag || "");
      setMetaTag(d.metaTag || "");
      setKeyword(d.keyword || "");
      setPrice(d.price ? String(d.price) : "");
      setQualityType(d.qualityType || "First Class");
      setDiscountActive(!!d.discountActive);
      setDiscountPrice(d.discountPrice ? String(d.discountPrice) : "0");
      setDiscription(d.discription || "");
      setBrandName(d.brandName || "");
      setStock(d.productStock !== undefined ? String(d.productStock) : "1");
      setOffers(d.offers || "");
      setIsdeliveryFree(!!d.isdeliveryFree);
      setShippingCharges(d.shippingCharges ? String(d.shippingCharges) : "0");
      setVideoUrl(d.videoUrl || "");
      setMaterials(d.materials || "");
      setHighlights(d.highlights || "");

      setCategoryName(d.category || "");
      setCategoryId(d.categoryId || "");
      setSubcategoryId(d.subcategoryId || "");
      setSubcategoryName(d.subcategory || "");
      setProductTypeId(d.productTypeId || "");
      setProductTypeName(d.productType || "");
      setCategoryPath([d.category, d.subcategory, d.productType].filter(Boolean).join(" → "));
      setTags(d.tags || []);
      setSelectedAttributes(d.categoryAttributes || {});
      setMainImages(d.mainImage || []);
      setGalleryImages(d.image || []);
      setLastSavedTime(new Date(d.lastSavedAt || d.updatedAt));
      setSaveStatus("saved");
      setShowResumeBanner(false);

      if (d.categoryId) {
        fetchCategoryAttributes(d.categoryId);
      }
    } catch (err) {
      setError("Failed to load draft.");
    } finally {
      setFetching(false);
    }
  }, [fetchCategoryAttributes]);

  // Check if vendor has active drafts when creating a new product
  const checkExistingDrafts = useCallback(async () => {
    try {
      if (initialDraftId) {
        loadDraft(initialDraftId);
        return;
      }
      const res = await api.get("/vendor/products/drafts");
      if (res.data.drafts && res.data.drafts.length > 0) {
        setAvailableDrafts(res.data.drafts);
        setShowResumeBanner(true);
      }
    } catch (err) {
      console.warn("Could not check drafts:", err);
    }
  }, [initialDraftId, loadDraft]);

  // Build draft payload
  const buildPayload = useCallback(() => {
    return {
      draftId: draftId || undefined,
      name,
      tag: tag || (tags.length > 0 ? tags[0] : categoryName),
      metaTag: metaTag || name,
      keyword: keyword || tags.join(", "),
      price: price ? Number(price) : 0,
      qualityType,
      discountActive,
      discountPrice: discountPrice ? Number(discountPrice) : 0,
      discription,
      brandName: brandName || "Generic",
      category: categoryName,
      categoryId: categoryId || null,
      subcategoryId: subcategoryId || null,
      subcategory: subcategoryName || "",
      productTypeId: productTypeId || null,
      productType: productTypeName || "",
      tags,
      categoryAttributes: selectedAttributes,
      customCategoryRequestId,
      productStock: stock !== undefined ? Number(stock) : 1,
      offers,
      isdeliveryFree,
      shippingCharges: shippingCharges ? Number(shippingCharges) : 0,
      videoUrl,
      materials,
      highlights,
      mainImage: mainImages,
      image: galleryImages
    };
  }, [
    draftId,
    name,
    tag,
    tags,
    categoryName,
    metaTag,
    keyword,
    price,
    qualityType,
    discountActive,
    discountPrice,
    discription,
    brandName,
    categoryId,
    subcategoryId,
    subcategoryName,
    productTypeId,
    productTypeName,
    selectedAttributes,
    customCategoryRequestId,
    stock,
    offers,
    isdeliveryFree,
    shippingCharges,
    videoUrl,
    materials,
    highlights,
    mainImages,
    galleryImages
  ]);

  // Execute Auto-Save to Server (or local backup if offline)
  const triggerAutoSave = useCallback(async () => {
    if (isEditMode) return; // Only auto-save drafts in creation mode

    const payload = buildPayload();

    // Check if at least one meaningful field exists
    if (!payload.name && !payload.category && !payload.price && !payload.discription && mainImages.length === 0) {
      return;
    }

    if (!navigator.onLine) {
      // Save locally to localStorage
      try {
        localStorage.setItem("im_vendor_draft_offline", JSON.stringify(payload));
        setSaveStatus("offline");
      } catch (e) {}
      return;
    }

    setSaveStatus("saving");
    try {
      const res = await api.post("/vendor/products/drafts", payload);
      if (res.data.draft) {
        setDraftId(res.data.draft._id);
        setLastSavedTime(new Date());
        setSaveStatus("saved");
      }
    } catch (err) {
      setSaveStatus("error");
    }
  }, [isEditMode, buildPayload, mainImages.length]);

  // Online / Offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setSaveStatus("saved");
      triggerAutoSave();
    };
    const handleOffline = () => {
      setSaveStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [triggerAutoSave]);

  // Fetch product if in Edit Mode
  useEffect(() => {
    if (isEditMode) {
      const fetchProduct = async () => {
        setFetching(true);
        try {
          const response = await api.get(`/vendor/products/${id}`);
          const p = response.data.product;
          setName(p.name || "");
          setTag(p.tag || "");
          setMetaTag(p.metaTag || "");
          setKeyword(p.keyword || "");
          setPrice(String(p.price || ""));
          setQualityType(p.qualityType || "First Class");
          setDiscountActive(!!p.discountActive);
          setDiscountPrice(String(p.discountPrice || 0));
          setDiscription(p.discription || "");
          setBrandName(p.brandName || "");
          setStock(String(p.productStock || 1));
          setOffers(p.offers || "");
          setIsdeliveryFree(!!p.isdeliveryFree);
          setShippingCharges(String(p.shippingCharges || 0));
          setVideoUrl(p.videoUrl || "");
          setMaterials(p.materials || "");
          setHighlights(p.highlights || "");

          setCategoryName(p.category || "");
          setCategoryId(p.categoryId || "");
          setSubcategoryId(p.subcategoryId || "");
          setSubcategoryName(p.subcategory || "");
          setProductTypeId(p.productTypeId || "");
          setProductTypeName(p.productType || "");
          setCategoryPath([p.category, p.subcategory, p.productType].filter(Boolean).join(" → "));
          setTags(p.tags || []);
          setSelectedAttributes(p.categoryAttributes || {});
          setMainImages(p.mainImage || []);
          setGalleryImages(p.image || []);

          if (p.categoryId) {
            fetchCategoryAttributes(p.categoryId);
          }
        } catch (err) {
          setError(err.response?.data?.msg || "Failed to fetch product details.");
        } finally {
          setFetching(false);
        }
      };
      fetchProduct();
    } else {
      // Check seller verification readiness
      const checkVerification = async () => {
        try {
          const res = await api.get("/vendor/verification/status");
          setReadiness(res.data);
        } catch (e) {}
      };
      checkVerification();
      // Check for unfinished drafts on new creation
      checkExistingDrafts();
    }
  }, [id, isEditMode, checkExistingDrafts, fetchCategoryAttributes]);

  // Debounced auto-save listener on field changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      triggerAutoSave();
    }, 750);

    return () => clearTimeout(autoSaveTimerRef.current);
  }, [
    name,
    price,
    categoryName,
    discription,
    brandName,
    stock,
    discountActive,
    discountPrice,
    tags,
    selectedAttributes,
    shippingCharges,
    isdeliveryFree,
    highlights,
    materials,
    triggerAutoSave
  ]);

  // Smart suggestion debounce when user types product name
  useEffect(() => {
    if (isEditMode || !name.trim() || name.length < 3) {
      setSuggestion(null);
      return;
    }

    if (suggestTimerRef.current) {
      clearTimeout(suggestTimerRef.current);
    }

    suggestTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/categories/suggest?name=${encodeURIComponent(name)}`);
        if (res.data.suggestions && res.data.suggestions.length > 0) {
          setSuggestion(res.data.suggestions[0]);
        } else {
          setSuggestion(null);
        }
      } catch (err) {
        setSuggestion(null);
      }
    }, 400);

    return () => clearTimeout(suggestTimerRef.current);
  }, [name, isEditMode]);

  const handleApplySuggestion = () => {
    if (!suggestion) return;
    setCategoryId(suggestion.categoryId);
    setCategoryName(suggestion.categoryName);
    setSubcategoryId(suggestion.subcategoryId || "");
    setSubcategoryName(suggestion.subcategoryName || "");
    setProductTypeId(suggestion.productTypeId || "");
    setProductTypeName(suggestion.productTypeName || "");
    setCategoryPath(suggestion.breadcrumbs || suggestion.categoryName);
    if (suggestion.tags?.length > 0) {
      setTags((prev) => Array.from(new Set([...prev, ...suggestion.tags])));
    }
    fetchCategoryAttributes(suggestion.categoryId);
    setSuggestion(null);
  };

  const handleSelectFromPicker = (selected) => {
    setCategoryId(selected.categoryId || "");
    setCategoryName(selected.categoryName || "");
    setSubcategoryId(selected.subcategoryId || "");
    setSubcategoryName(selected.subcategoryName || "");
    setProductTypeId(selected.productTypeId || "");
    setProductTypeName(selected.productTypeName || "");
    setCategoryPath(selected.fullPath || selected.categoryName);
    setCustomCategoryRequestId(selected.customCategoryRequestId || null);
    if (selected.tags?.length > 0) {
      setTags((prev) => Array.from(new Set([...prev, ...selected.tags])));
    }
    if (selected.categoryId) {
      fetchCategoryAttributes(selected.categoryId);
    }
    setSuggestion(null);
  };

  // Immediate Image Upload Handler (Uploads file to server & links to draft)
  const handleImageUpload = async (e, isHead = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    setSaveStatus("saving");
    try {
      const formData = new FormData();
      if (draftId) {
        formData.append("draftId", draftId);
      }
      formData.append("imageType", isHead ? "headImage" : "image");

      if (isHead) {
        formData.append("headImage", files[0]);
      } else {
        files.forEach((f) => formData.append("image", f));
      }

      const res = await api.post("/vendor/products/drafts/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data.uploadedItems) {
        if (isHead) {
          setMainImages(res.data.uploadedItems);
        } else {
          setGalleryImages((prev) => [...prev, ...res.data.uploadedItems]);
        }
      }
      if (res.data.draft) {
        setDraftId(res.data.draft._id);
      }
      setSaveStatus("saved");
      setLastSavedTime(new Date());
    } catch (err) {
      alert("Failed to upload image. Please try again.");
      setSaveStatus("error");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveMainImage = () => {
    setMainImages([]);
  };

  const handleRemoveGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAttributeChange = (attrName, value) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attrName]: value
    }));
  };

  const handleAddCustomTag = (e) => {
    if (e.key === "Enter" || e.type === "click") {
      e.preventDefault();
      const val = customTagInput.trim();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
        setCustomTagInput("");
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // Discard draft confirmation
  const handleDiscardDraft = async () => {
    if (!window.confirm("Are you sure you want to discard this draft? All unsaved progress will be removed.")) {
      return;
    }
    try {
      if (draftId) {
        await api.delete(`/vendor/products/drafts/${draftId}`);
      }
      localStorage.removeItem("im_vendor_draft_offline");
      history.push("/vendor/products");
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to discard draft.");
    }
  };

  // Submit Draft For Review (Publish / Submission Flow)
  const handleSubmitForReview = async (e) => {
    e.preventDefault();
    setError("");
    setValidationErrors([]);

    // Client-side validation checklist
    const errors = [];
    if (!name || name.trim().length < 3) errors.push("Product Name is required (min 3 chars).");
    if (!categoryName) errors.push("Please select a Product Category.");
    if (!price || Number(price) <= 0) errors.push("Valid Selling Price is required.");
    if (mainImages.length === 0) errors.push("Main Header Image is required.");
    if (!discription || discription.trim().length < 10) errors.push("Product Description is required (min 10 chars).");

    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmittingReview(true);
    try {
      if (isEditMode) {
        // Edit mode direct update
        const payload = buildPayload();
        await api.put(`/vendor/products/${id}`, payload);
        history.push("/vendor/products");
      } else {
        // Make sure latest state is saved in draft first
        const payload = buildPayload();
        const saveRes = await api.post("/vendor/products/drafts", payload);
        const targetDraftId = saveRes.data.draft?._id || draftId;

        // Submit draft for review
        await api.post(`/vendor/products/drafts/${targetDraftId}/submit`);
        localStorage.removeItem("im_vendor_draft_offline");
        history.push("/vendor/products?tab=pending");
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to submit product for review.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmittingReview(false);
    }
  };

  // Calculate completion percentage
  const calculateCompletionPercent = () => {
    let score = 0;
    if (name && name.trim().length >= 3) score += 20;
    if (categoryName) score += 20;
    if (price && Number(price) > 0) score += 20;
    if (mainImages.length > 0) score += 20;
    if (discription && discription.trim().length >= 10) score += 10;
    if (stock && Number(stock) >= 0) score += 10;
    return Math.min(100, score);
  };

  const completionPct = calculateCompletionPercent();

  if (fetching) {
    return (
      <div>
        <div className="skeleton" style={{ height: "32px", width: "220px", marginBottom: "8px" }} />
        <div className="skeleton" style={{ height: "600px", borderRadius: "12px" }} />
      </div>
    );
  }

  // Mandatory Seller Verification Gate
  if (!isEditMode && readiness && !readiness.isEligible) {
    return <VerificationGate readiness={readiness} title="Complete Seller Verification to Add Products" />;
  }

  return (
    <div>
      {/* Category Picker Modal */}
      <CategoryPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelectCategory={handleSelectFromPicker}
      />

      {/* Resume Unfinished Draft Banner */}
      {showResumeBanner && availableDrafts.length > 0 && (
        <div
          style={{
            background: "#eff6ff",
            border: "1.5px solid #bfdbfe",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            boxShadow: "0 2px 8px rgba(37,99,235,0.05)"
          }}
        >
          <div>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#1d4ed8", textTransform: "uppercase" }}>
              RESUME UNFINISHED DRAFT
            </span>
            <h4 style={{ fontSize: "15px", fontWeight: "900", color: "#1e3a8a", margin: "2px 0 0" }}>
              You have an unfinished product draft: &ldquo;{availableDrafts[0].name || "Untitled Product"}&rdquo; ({availableDrafts[0].completionPercentage || 40}% complete)
            </h4>
            <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
              Last saved {new Date(availableDrafts[0].updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => loadDraft(availableDrafts[0]._id)}
              className="btn btn-primary"
              style={{ fontSize: "12.5px", padding: "7px 16px" }}
            >
              Continue Editing &rarr;
            </button>
            <button
              type="button"
              onClick={() => setShowResumeBanner(false)}
              className="btn btn-outline-secondary"
              style={{ fontSize: "12.5px", padding: "7px 14px" }}
            >
              Start New Product
            </button>
          </div>
        </div>
      )}

      {/* Page Header with Real-Time Auto-Save Status */}
      <div className="page-header" style={{ marginBottom: "16px" }}>
        <div className="page-header-left">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <h2>{isEditMode ? "Edit Product" : "Add New Product"}</h2>

            {/* Save Status Indicator */}
            {!isEditMode && (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "3px 10px",
                  borderRadius: "99px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background:
                    saveStatus === "saving"
                      ? "#fef3c7"
                      : saveStatus === "offline"
                      ? "#fee2e2"
                      : saveStatus === "error"
                      ? "#fee2e2"
                      : "#dcfce7",
                  color:
                    saveStatus === "saving"
                      ? "#92400e"
                      : saveStatus === "offline"
                      ? "#991b1b"
                      : saveStatus === "error"
                      ? "#991b1b"
                      : "#166534"
                }}
              >
                {saveStatus === "saving" && "⟳ Saving..."}
                {saveStatus === "saved" && `✓ Saved ${lastSavedTime ? lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "just now"}`}
                {saveStatus === "offline" && "Offline — saved locally"}
                {saveStatus === "error" && "⚠ Couldn't save"}
                {saveStatus === "idle" && "Draft Auto-Save Ready"}
              </span>
            )}
          </div>
          <p>{isEditMode ? "Update your product details." : "Your progress is automatically saved to your draft as you type."}</p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {!isEditMode && draftId && (
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="btn btn-outline-secondary"
              style={{ fontSize: "12.5px", padding: "8px 14px", color: "#dc2626", borderColor: "#fecaca" }}
            >
              Discard Draft
            </button>
          )}
          <button
            type="button"
            onClick={() => history.push("/vendor/products")}
            className="btn btn-outline-secondary"
            style={{ fontSize: "12.5px", padding: "8px 16px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="product-form"
            className="btn btn-primary"
            disabled={submittingReview || uploadingImage}
            style={{ fontSize: "12.5px", padding: "8px 20px", fontWeight: "800" }}
          >
            {submittingReview ? "Submitting..." : isEditMode ? "Save Changes" : "Submit for Review &rarr;"}
          </button>
        </div>
      </div>

      {/* Validation Errors Alert */}
      {validationErrors.length > 0 && (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px" }}>
          <strong style={{ fontSize: "13.5px", color: "#991b1b", display: "block", marginBottom: "4px" }}>
            Please complete the following required fields before submitting:
          </strong>
          <ul style={{ margin: "4px 0 0", paddingLeft: "20px", color: "#b91c1c", fontSize: "13px" }}>
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", padding: "14px 16px", marginBottom: "20px", color: "#dc2626", fontSize: "14px" }}>
          ⚠ {error}
        </div>
      )}

      <form id="product-form" onSubmit={handleSubmitForReview}>
        <div className="row g-4">
          {/* Main Column */}
          <div className="col-lg-8">
            {/* 1. BASIC INFORMATION & SMART CATEGORY SUGGESTION */}
            <div className="card-brand p-4" style={{ marginBottom: "20px" }}>
              <SectionTitle>1. BASIC INFORMATION</SectionTitle>

              <div className="row">
                <div className="col-md-8">
                  <Field label="Product Name" required hint="A clear, descriptive name helps buyers discover your item.">
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Samsung Galaxy S24 5G, Levi's 511 Slim Fit Jeans"
                    />
                  </Field>
                </div>
                <div className="col-md-4">
                  <Field label="Brand Name" hint="Brand or manufacturer.">
                    <input
                      type="text"
                      className="form-control"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="e.g. Samsung, Nike, Handcrafted"
                    />
                  </Field>
                </div>
              </div>

              {/* Smart Recommendation Banner */}
              {suggestion && !categoryName && (
                <div
                  style={{
                    background: "#ecfdf5",
                    border: "1.5px solid #a7f3d0",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    marginBottom: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "10px"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <SparklesIcon size={18} color="#065f46" />
                    <div>
                      <span style={{ fontSize: "11px", fontWeight: "800", color: "#065f46", textTransform: "uppercase" }}>
                        RECOMMENDED CATEGORY ({suggestion.confidence}% Match)
                      </span>
                      <strong style={{ fontSize: "13.5px", color: "#064e3b", display: "block" }}>
                        {suggestion.breadcrumbs}
                      </strong>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={handleApplySuggestion}
                      className="btn btn-primary"
                      style={{ padding: "6px 14px", fontSize: "12px", background: "#059669", borderColor: "#059669" }}
                    >
                      Use Recommended Category &rarr;
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsPickerOpen(true)}
                      className="btn btn-outline-secondary"
                      style={{ padding: "6px 12px", fontSize: "12px" }}
                    >
                      Browse
                    </button>
                  </div>
                </div>
              )}

              {/* Category Selection Card */}
              <div
                style={{
                  background: "#fbfaf8",
                  border: "1.5px solid #edf0f4",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "18px"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <label className="form-label" style={{ margin: 0, fontWeight: "800", color: "#111315" }}>
                    Where Does Your Product Belong? <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsPickerOpen(true)}
                    className="btn btn-outline-primary"
                    style={{ padding: "5px 12px", fontSize: "12px", fontWeight: "800" }}
                  >
                    {categoryName ? "Change Category" : "+ Select Category"}
                  </button>
                </div>

                {categoryName ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#ffffff", border: "1px solid #edf0f4", padding: "10px 14px", borderRadius: "8px" }}>
                    <CheckCircleIcon size={16} color="#16a34a" />
                    <span style={{ fontSize: "13.5px", fontWeight: "800", color: "#111315" }}>
                      {categoryPath || categoryName}
                    </span>
                  </div>
                ) : (
                  <div
                    onClick={() => setIsPickerOpen(true)}
                    style={{
                      border: "1.5px dashed #d1d5db",
                      borderRadius: "8px",
                      padding: "14px",
                      textAlign: "center",
                      cursor: "pointer",
                      color: "var(--text-muted)",
                      fontSize: "13px"
                    }}
                  >
                    Click to search or browse categories across Fashion, Electronics, Footwear, Home & Kitchen, and 25+ more departments.
                  </div>
                )}
              </div>

              {/* Description & Highlights */}
              <Field label="Description" required hint="Describe your product: materials, specifications, and key features.">
                <textarea
                  className="form-control"
                  rows="4"
                  required
                  value={discription}
                  onChange={(e) => setDiscription(e.target.value)}
                  placeholder="Tell buyers what makes this product great..."
                />
              </Field>

              <Field label="Highlights / Key Bullet Points" hint="Separate key bullet points with commas.">
                <textarea
                  className="form-control"
                  rows="2"
                  value={highlights}
                  onChange={(e) => setHighlights(e.target.value)}
                  placeholder="5G Enabled, 100% Cotton, 1 Year Warranty, Fast Delivery"
                />
              </Field>
            </div>

            {/* 2. DYNAMIC CATEGORY ATTRIBUTES */}
            {categoryAttributesDef.length > 0 && (
              <div className="card-brand p-4" style={{ marginBottom: "20px" }}>
                <SectionTitle>2. CATEGORY SPECIFICATIONS &amp; ATTRIBUTES</SectionTitle>
                <p style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "-6px", marginBottom: "16px" }}>
                  Providing these specifications helps customers filter and find your product in marketplace searches.
                </p>

                <div className="row">
                  {categoryAttributesDef.map((attr, idx) => (
                    <div key={idx} className="col-md-6">
                      <Field label={attr.name} required={attr.isRequired}>
                        {attr.type === "select" && attr.options?.length > 0 ? (
                          <select
                            className="form-select"
                            required={attr.isRequired}
                            value={selectedAttributes[attr.name] || ""}
                            onChange={(e) => handleAttributeChange(attr.name, e.target.value)}
                          >
                            <option value="">Select {attr.name}</option>
                            {attr.options.map((opt, optIdx) => (
                              <option key={optIdx} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={attr.type === "number" ? "number" : "text"}
                            className="form-control"
                            required={attr.isRequired}
                            placeholder={attr.placeholder || `Enter ${attr.name}`}
                            value={selectedAttributes[attr.name] || ""}
                            onChange={(e) => handleAttributeChange(attr.name, e.target.value)}
                          />
                        )}
                      </Field>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. PRODUCT TAGS */}
            <div className="card-brand p-4" style={{ marginBottom: "20px" }}>
              <SectionTitle>3. SEARCH TAGS &amp; KEYWORDS</SectionTitle>
              <p style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "-6px", marginBottom: "14px" }}>
                Tags make your product discoverable when buyers type related keywords in search.
              </p>

              {/* Tag Pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
                {tags.map((t, idx) => (
                  <span
                    key={idx}
                    style={{
                      background: "#fff5f4",
                      border: "1px solid #fecaca",
                      color: "var(--primary)",
                      fontSize: "12px",
                      fontWeight: "700",
                      padding: "4px 10px",
                      borderRadius: "99px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                  >
                    <span>#{t}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(t)}
                      style={{ background: "transparent", border: "none", color: "var(--primary)", cursor: "pointer", padding: 0, fontSize: "12px", fontWeight: "900" }}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>

              {/* Add Custom Tag */}
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ maxWidth: "320px", fontSize: "13px" }}
                  placeholder="Add search tag (e.g. 'Cotton', '5G', 'Gift')"
                  value={customTagInput}
                  onChange={(e) => setCustomTagInput(e.target.value)}
                  onKeyDown={handleAddCustomTag}
                />
                <button
                  type="button"
                  onClick={handleAddCustomTag}
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "12.5px" }}
                >
                  + Add Tag
                </button>
              </div>
            </div>

            {/* 4. PRODUCT IMAGES (Auto-Upload to Server) */}
            <div className="card-brand p-4" style={{ marginBottom: "20px" }}>
              <SectionTitle>4. PRODUCT IMAGES</SectionTitle>
              <p style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "-6px", marginBottom: "16px" }}>
                Photos are uploaded and attached immediately to your draft so you never lose them upon refresh.
              </p>

              <div className="row g-3">
                {/* Main Header Image */}
                <div className="col-md-6">
                  <label className="form-label">
                    Main Header Image <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  {mainImages.length > 0 ? (
                    <div style={{ position: "relative", width: "100%", height: "160px", borderRadius: "10px", overflow: "hidden", border: "1px solid #edf0f4" }}>
                      <img
                        src={getFullImageUrl(mainImages[0].imageurl)}
                        alt="Main Preview"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveMainImage}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "rgba(0,0,0,0.65)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div style={{ border: "1.5px dashed #cbd5e1", borderRadius: "10px", padding: "20px", textAlign: "center", background: "#fbfaf8" }}>
                      <input
                        type="file"
                        id="main-img-input"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleImageUpload(e, true)}
                      />
                      <label htmlFor="main-img-input" style={{ cursor: "pointer", display: "block" }}>
                        <div style={{ marginBottom: "6px" }}>
                          <CameraIcon size={28} color="var(--primary)" />
                        </div>
                        <strong style={{ fontSize: "13px", color: "var(--primary)" }}>Upload Header Photo</strong>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>PNG, JPG up to 5MB</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Catalog Gallery Images */}
                <div className="col-md-6">
                  <label className="form-label">Gallery Images</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {galleryImages.map((img, idx) => (
                      <div key={idx} style={{ position: "relative", width: "70px", height: "70px", borderRadius: "8px", overflow: "hidden", border: "1px solid #edf0f4" }}>
                        <img
                          src={getFullImageUrl(img.imageurl)}
                          alt={`Gallery ${idx}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(idx)}
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "grid",
                            placeItems: "center",
                            cursor: "pointer"
                          }}
                        >
                          <XIcon size={12} color="#ffffff" />
                        </button>
                      </div>
                    ))}

                    <div style={{ width: "70px", height: "70px", border: "1.5px dashed #cbd5e1", borderRadius: "8px", display: "grid", placeItems: "center", background: "#fbfaf8" }}>
                      <input
                        type="file"
                        id="gallery-img-input"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => handleImageUpload(e, false)}
                      />
                      <label htmlFor="gallery-img-input" style={{ cursor: "pointer", display: "grid", placeItems: "center", width: "100%", height: "100%" }}>
                        <PlusIcon size={20} color="var(--text-muted)" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar: Pricing, Inventory & Completion Checklist */}
          <div className="col-lg-4">
            <div className="card-brand p-4" style={{ marginBottom: "20px" }}>
              <SectionTitle>PRICING &amp; INVENTORY</SectionTitle>

              <Field label="Selling Price (₹)" required>
                <input
                  type="number"
                  className="form-control"
                  required
                  min="1"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="999"
                />
              </Field>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={discountActive}
                    onChange={(e) => setDiscountActive(e.target.checked)}
                  />
                  <span>Enable Discounted Price</span>
                </label>
              </div>

              {discountActive && (
                <Field label="Discount / Sale Price (₹)">
                  <input
                    type="number"
                    className="form-control"
                    value={discountPrice}
                    onChange={(e) => setDiscountPrice(e.target.value)}
                    placeholder="799"
                  />
                </Field>
              )}

              <Field label="Stock Quantity (Units)" required>
                <input
                  type="number"
                  className="form-control"
                  required
                  min="0"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="10"
                />
              </Field>

              <Field label="Quality Grade">
                <select
                  className="form-select"
                  value={qualityType}
                  onChange={(e) => setQualityType(e.target.value)}
                >
                  <option value="First Class">First Class</option>
                  <option value="Premium">Premium</option>
                  <option value="Standard">Standard</option>
                  <option value="Budget">Budget</option>
                </select>
              </Field>

              <div style={{ borderTop: "1px solid #edf0f4", paddingTop: "14px", marginTop: "14px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", marginBottom: "10px" }}>
                  <input
                    type="checkbox"
                    checked={isdeliveryFree}
                    onChange={(e) => setIsdeliveryFree(e.target.checked)}
                  />
                  <span>Free Delivery for Customers</span>
                </label>

                {!isdeliveryFree && (
                  <Field label="Shipping Charges (₹)">
                    <input
                      type="number"
                      className="form-control"
                      value={shippingCharges}
                      onChange={(e) => setShippingCharges(e.target.value)}
                      placeholder="50"
                    />
                  </Field>
                )}
              </div>
            </div>

            {/* PRODUCT COMPLETION TRACKER */}
            <div className="card-brand p-4">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <SectionTitle>DRAFT COMPLETION</SectionTitle>
                <span style={{ fontSize: "13px", fontWeight: "900", color: completionPct === 100 ? "#16a34a" : "var(--primary)" }}>
                  {completionPct}%
                </span>
              </div>

              {/* Progress Bar */}
              <div style={{ background: "#f0f2f5", height: "8px", borderRadius: "99px", overflow: "hidden", marginBottom: "14px" }}>
                <div
                  style={{
                    background: completionPct === 100 ? "#16a34a" : "var(--primary)",
                    width: `${completionPct}%`,
                    height: "100%",
                    transition: "width 0.3s ease"
                  }}
                />
              </div>

              {/* Checklist */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "12.5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: name ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{name ? "✓" : "○"}</span> Product Name
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: categoryName ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{categoryName ? "✓" : "○"}</span> Taxonomy Category
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: mainImages.length > 0 ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{mainImages.length > 0 ? "✓" : "○"}</span> Header Photo
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: Number(price) > 0 ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{Number(price) > 0 ? "✓" : "○"}</span> Selling Price
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: discription.length >= 10 ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{discription.length >= 10 ? "✓" : "○"}</span> Description (min 10 chars)
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: Number(stock) >= 0 ? "#16a34a" : "var(--text-muted)" }}>
                  <span>{Number(stock) >= 0 ? "✓" : "○"}</span> Stock Inventory
                </div>
              </div>
            </div>

            {/* Desktop & Tablet Bottom Action Card */}
            <div className="card-brand p-3 d-none d-md-block" style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", alignItems: "center" }}>
                {!isEditMode && draftId && (
                  <button
                    type="button"
                    onClick={handleDiscardDraft}
                    className="btn btn-outline-secondary"
                    style={{ fontSize: "12px", padding: "8px 14px", color: "#dc2626", borderColor: "#fecaca" }}
                  >
                    Discard Draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => history.push("/vendor/products")}
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "12px", padding: "8px 14px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="product-form"
                  className="btn btn-primary"
                  disabled={submittingReview || uploadingImage}
                  style={{ fontSize: "12.5px", padding: "8px 18px", fontWeight: "800" }}
                >
                  {submittingReview ? "Submitting..." : isEditMode ? "Save Changes" : "Submit for Review &rarr;"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Mobile Sticky Bottom Action Bar for Smart Phones */}
      <div className="mobile-product-sticky-bar d-md-none">
        <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <span style={{
            fontSize: "11px",
            fontWeight: "750",
            padding: "4px 8px",
            borderRadius: "99px",
            background: saveStatus === "saving" ? "#fef3c7" : "#dcfce7",
            color: saveStatus === "saving" ? "#92400e" : "#166534",
            whiteSpace: "nowrap"
          }}>
            {saveStatus === "saving" ? "⟳ Saving..." : saveStatus === "saved" ? "✓ Saved" : `${completionPct}% Complete`}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            type="button"
            onClick={() => history.push("/vendor/products")}
            className="btn btn-outline-secondary"
            style={{ fontSize: "12px", padding: "8px 12px", borderRadius: "8px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="product-form"
            className="btn btn-primary"
            disabled={submittingReview || uploadingImage}
            style={{ fontSize: "12px", padding: "8px 16px", fontWeight: "800", borderRadius: "8px", whiteSpace: "nowrap" }}
          >
            {submittingReview ? "..." : isEditMode ? "Save" : "Submit &rarr;"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductForm;
